import { SaveMode } from "../enums/SaveModeEnum";

export const getLastSplittedStringItem = (fileName: string, splitBy: RegExp) => {
    const pieces = fileName?.split(splitBy) ?? [];
    return pieces.length > 0 ? pieces[pieces.length - 1] : "";
}

export const getExtension = (name: string): string => {
    if (!name) return "";

    const lastDotIndex = name.lastIndexOf(".");
    if (lastDotIndex === -1 || lastDotIndex === name.length - 1) {
        return "";
    }
    return name.substring(lastDotIndex + 1);
};

export const removeExtension = (filename: string) => {
    if (!filename) return "";

    const lastDotIndex = filename.lastIndexOf(".");
    if (lastDotIndex <= 0) {
        return filename;
    }
    return filename.substring(0, lastDotIndex);
}

export const getRelativeDocumentUrl = (url: string, isRoot: boolean) => {
    let array = url?.split("/") ?? [];
    let part = array.slice(Math.max(array.length - (isRoot ? 1 : 2), 1))
    return part.join("/");
}

export const truncateWithEllipses = (text: string, max: number) => {
    return text.substr(0, max - 1) + (text.length > max ? ' ...' : '');
}

export const createUniqueKey = () => '_' + Math.random().toString(36).substr(2, 9);

export const localStringToNumber = (textNumber: string) => {
    return Number(String(textNumber).replace(/[^0-9.-]+/g, ""));
};

export const changeExtensionToPdf = (filename: string): string => {
    if (filename) {
        let dotIndex = filename.lastIndexOf(".");
        if (dotIndex === -1) return filename + '.pdf'; // If there's no extension, just append .pdf
        return filename.substring(0, dotIndex) + '.pdf';
    }
    return "";
}

// A dragged Outlook/desktop .eml is staged on the file-system upload path
// (isFileSystemEntry). When the tenant convertEmailToPDF setting is on, the backend
// converts those .eml message entries to PDF and stores them with a .pdf name — see
// LocalUploadedFilesService.GetContentAsync, whose gate is exactly
// `convertEmailToPDF && extension == "eml"` on the file-system path. This predicate lets
// the client's duplicate-detection existence check compute the SAME .pdf name the backend
// will produce, so a pre-existing "<subject>.pdf" is correctly flagged as a duplicate.
// It intentionally does NOT match the mail-item path (isMainMailItem) or attachments —
// those are handled by their own existing clauses / stay in their original format. The
// param is a structural subset (not IFileToUpload) to keep this helper decoupled and
// null-safe.
export const shouldConvertDroppedEmailToPdf = (
    item: { isFileSystemEntry?: boolean; extension?: string },
    convertEmailToPDF: boolean
): boolean => {
    return convertEmailToPDF === true
        && item?.isFileSystemEntry === true
        && item?.extension?.trim().toLowerCase() === "eml";
}

// An item the "Emails as PDF" save format applies to: the open mail message or
// an .eml file (dropped, or copied from Exchange in the content manager). Drives
// which SaveFormatChoice options render — "Emails as PDF" needs one of these in
// the selection, "Everything as PDF" needs a convertible item that is NOT one
// (otherwise the two formats produce identical results).
export const isEmailFormatItem = (
    item: { isMainMailItem?: boolean; extension?: string }
): boolean => {
    return item?.isMainMailItem === true
        || item?.extension?.trim().toLowerCase() === "eml";
}

// Whether the backend will store this item as .pdf under the given save flags —
// the union of the four server conversion gates: the mail-item gate
// (convertEmailToPDF, EmailGraphService.GetEmailContents), the graph-mail gate
// (content-manager Exchange copies, EmailGraphService.GetGraphEmailContents —
// the email message converts, its attachments only under SaveAsPdf), the
// SaveAsPdf gate (saveMode + CanSaveAsPDF, all paths) and the dropped-.eml gate
// (LocalUploadedFilesService).
// Every place that computes or displays the final filename (duplicate checks,
// row extension/icon previews) must use this single predicate so what the user
// sees, what is duplicate-checked, and what the server stores never diverge.
export const willSaveAsPdf = (
    item: { isMainMailItem?: boolean; isFileSystemEntry?: boolean; isGraphMailItem?: boolean; isAttachment?: boolean; extension?: string; canSaveAsPDF?: boolean },
    convertEmailToPDF: boolean,
    saveMode?: SaveMode
): boolean => {
    return (item?.isMainMailItem === true && convertEmailToPDF === true)
        || (item?.isGraphMailItem === true && item?.isAttachment !== true && convertEmailToPDF === true)
        || (saveMode === SaveMode.SaveAsPdf && item?.canSaveAsPDF === true)
        || shouldConvertDroppedEmailToPdf(item, convertEmailToPDF);
}

export const splitName = (fullName: string): { firstName: string, lastName: string } => {
    if (fullName) {
        let nameParts = fullName.trim().split(' ');

        let firstName: string = nameParts?.shift() || '';
        let lastName: string = nameParts?.join(' ');

        return { firstName, lastName };
    } else {
        return { firstName: '', lastName: '' };
    }
}

export const formatGuidString = (value: string): string => {
    if (value && !value.includes("-")) {
        const parts = [];
        parts.push(value.slice(0, 8));
        parts.push(value.slice(8, 12));
        parts.push(value.slice(12, 16));
        parts.push(value.slice(16, 20));
        parts.push(value.slice(20, 32));
        return parts.join("-");
    }
    return value;
}

export const splitStringByNewline = (input: string): string[] => {
    if (!input) return [];
    return input.split(/\r\n|\r|\n/);
}

// Mirrors Office.js `normalizedSubject`: strips leading reply/forward prefixes
// so Graph-sourced subjects map to the same SharePoint column value as the
// single-item Office.js path (incl. the NL/DE prefixes Outlook localizes to).
const subjectPrefixPattern = /^(re|fw|fwd|aw|wg|antw)\s*:\s*/i;
export const normalizeEmailSubject = (subject: string): string => {
    if (typeof subject !== "string") {
        return subject;
    }
    let normalized = subject.trim();
    while (subjectPrefixPattern.test(normalized)) {
        normalized = normalized.replace(subjectPrefixPattern, "").trim();
    }
    // A subject consisting solely of prefixes (e.g. "FW:") must not normalize
    // away to nothing — an empty result would read as "no subject" downstream
    // and the mapped column would be cleared. Fall back to the original.
    return normalized.length > 0 ? normalized : subject.trim();
}

export const trimCharacter = (str: string, charToTrim: string): string => {
    if (!str || !charToTrim || charToTrim.length !== 1) return str;

    const len = str.length;
    if (len === 0) {
        return "";
    }

    let startIndex = 0;
    while (startIndex < len && str[startIndex] === charToTrim) {
        startIndex++;
    }

    if (startIndex === len) {
        return "";
    }

    let endIndex = len;
    while (endIndex > startIndex && str[endIndex - 1] === charToTrim) {
        endIndex--;
    }

    if (startIndex === 0 && endIndex === len) {
        return str;
    }

    return str.slice(startIndex, endIndex);
}